import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  ReactNode,
} from 'react'

/**
 * Per-property saved-state store. Mirrors hoverContext: a stable external store
 * with per-id subscriptions, so toggling one property's saved-state re-renders
 * only that card — not the whole (virtualized) list. The list keeps stable
 * props and never re-renders on save, which keeps scrolling smooth.
 *
 * `overrides` holds the optimistic/post-auth saved-state layered on top of the
 * server value each listing already carries. A card with no override falls back
 * to `listing.primary_property.is_currently_saved`.
 */
type SavedSubscriber = (value: boolean) => void
interface SavedStateStore {
  subscribe: (id: string, cb: SavedSubscriber) => () => void
  setSaved: (id: string, value: boolean) => void
  get: (id: string) => boolean | undefined
}

const SavedStateContext = createContext<SavedStateStore | null>(null)

export function createSavedStateStore(): SavedStateStore {
  const overrides = new Map<string, boolean>()
  const subscribers = new Map<string, Set<SavedSubscriber>>()
  return {
    subscribe(id, cb) {
      let set = subscribers.get(id)
      if (!set) {
        set = new Set()
        subscribers.set(id, set)
      }
      set.add(cb)
      return () => {
        subscribers.get(id)?.delete(cb)
      }
    },
    setSaved(id, value) {
      if (overrides.get(id) === value) return
      overrides.set(id, value)
      subscribers.get(id)?.forEach(cb => cb(value))
    },
    get(id) {
      return overrides.get(id)
    },
  }
}

export function SavedStateProvider({
  store,
  children,
}: {
  store: SavedStateStore
  children: ReactNode
}) {
  return (
    <SavedStateContext.Provider value={store}>
      {children}
    </SavedStateContext.Provider>
  )
}

/**
 * Returns the saved-state for a property, subscribing to its own changes.
 * Only this card re-renders when the property's saved-state is toggled.
 */
export function useSavedState(id: string, fallback: boolean): boolean {
  const store = useContext(SavedStateContext)
  const [override, setOverride] = useState<boolean | undefined>(() =>
    store?.get(id)
  )
  useEffect(() => {
    if (!store || !id) return undefined
    setOverride(store.get(id))
    return store.subscribe(id, setOverride)
  }, [store, id])
  return override ?? fallback
}

export { SavedStateContext }
